import { buildMagicMask, buildMagicMaskKey } from '../canvas/tools/magicEraserTool';
import { getMaskCache, setMaskCache } from './maskCache';

const CACHE_LIMIT = 24;
const renderCache = new Map();

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const round = (value, precision = 3) =>
  Number.parseFloat(Number(value).toFixed(precision));

const serializeAdjust = (adjust = {}) => {
  const keys = [
    'brightness',
    'contrast',
    'saturation',
    'warmth',
    'blur',
    'temperature',
    'tint',
    'highlights',
    'shadows',
    'whites',
    'blacks',
    'vibrance',
    'invert',
    'sharpness',
    'clarity',
    'vignette',
  ];
  return keys
    .map((key) => `${key}:${key === 'invert' ? Boolean(adjust[key]) : round(adjust[key] ?? 0)}`)
    .join('|');
};

const serializeCrop = (crop = {}) =>
  `${round(crop.x || 0)}:${round(crop.y || 0)}:${round(crop.w || 1)}:${round(crop.h || 1)}:${
    crop.enabled ? '1' : '0'
  }`;

const serializeEraser = (eraser = {}) => {
  const strokeKey = eraser?.strokes?.length
    ? eraser.strokes
        .map((stroke) =>
          `${stroke.id}:${stroke.points?.length || 0}:${round(stroke.size || 0, 2)}:${round(
            stroke.opacity || 0,
            2,
          )}`,
        )
        .join('|')
    : 'none';
  const magicOps = eraser?.magic?.ops || [];
  const magicKey = magicOps.length
    ? magicOps
        .map((op) =>
          `${op.id}:${round(op.x || 0, 3)}:${round(op.y || 0, 3)}:${round(
            op.tolerance || 0,
            2,
          )}:${op.contiguous ? 1 : 0}:${round(op.feather || 0, 2)}:${round(
            op.expand || 0,
            2,
          )}:${op.opMode === 'subtract' ? 's' : 'a'}`,
        )
        .join('|')
    : 'none';
  return `strokes:${strokeKey}|magic:${magicKey}`;
};

export const buildAdjustCacheKey = ({
  assetId,
  src,
  crop,
  flipX,
  flipY,
  adjust,
  eraser,
  targetWidth,
  targetHeight,
  maxSize,
  fit,
}) => [
  assetId || src,
  `size:${targetWidth}x${targetHeight}`,
  `max:${maxSize}`,
  `fit:${fit || 'cover'}`,
  `crop:${serializeCrop(crop)}`,
  `flip:${flipX ? 1 : 0}${flipY ? 1 : 0}`,
  `adjust:${serializeAdjust(adjust)}`,
  `eraser:${serializeEraser(eraser)}`,
].join('|');

const rgbToHsl = (r, g, b) => {
  const nr = r / 255;
  const ng = g / 255;
  const nb = b / 255;
  const max = Math.max(nr, ng, nb);
  const min = Math.min(nr, ng, nb);
  const delta = max - min;
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (delta !== 0) {
    s = l > 0.5 ? delta / (2 - max - min) : delta / (max + min);
    switch (max) {
      case nr:
        h = (ng - nb) / delta + (ng < nb ? 6 : 0);
        break;
      case ng:
        h = (nb - nr) / delta + 2;
        break;
      default:
        h = (nr - ng) / delta + 4;
        break;
    }
    h /= 6;
  }

  return [h, s, l];
};

const hslToRgb = (h, s, l) => {
  if (s === 0) {
    const v = Math.round(l * 255);
    return [v, v, v];
  }

  const hue2rgb = (p, q, t) => {
    let tt = t;
    if (tt < 0) tt += 1;
    if (tt > 1) tt -= 1;
    if (tt < 1 / 6) return p + (q - p) * 6 * tt;
    if (tt < 1 / 2) return q;
    if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
    return p;
  };

  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const r = hue2rgb(p, q, h + 1 / 3);
  const g = hue2rgb(p, q, h);
  const b = hue2rgb(p, q, h - 1 / 3);
  return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
};

const smoothstep = (edge0, edge1, value) => {
  const t = clamp((value - edge0) / (edge1 - edge0), 0, 1);
  return t * t * (3 - 2 * t);
};

const applyToneAdjustment = (r, g, b, luma, value, start, end, strength) => {
  if (!value) return [r, g, b];
  const weight = smoothstep(start, end, luma);
  if (weight <= 0) return [r, g, b];
  const delta = (value / 100) * 255 * strength * weight;
  return [r + delta, g + delta, b + delta];
};

const boxBlur = (data, width, height, radius) => {
  if (radius <= 0) return new Uint8ClampedArray(data);
  const output = new Uint8ClampedArray(data.length);
  const size = radius * 2 + 1;
  const area = size * size;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      let r = 0;
      let g = 0;
      let b = 0;
      const base = (y * width + x) * 4;
      for (let ky = -radius; ky <= radius; ky += 1) {
        const yy = clamp(y + ky, 0, height - 1);
        for (let kx = -radius; kx <= radius; kx += 1) {
          const xx = clamp(x + kx, 0, width - 1);
          const index = (yy * width + xx) * 4;
          r += data[index];
          g += data[index + 1];
          b += data[index + 2];
        }
      }
      output[base] = r / area;
      output[base + 1] = g / area;
      output[base + 2] = b / area;
      output[base + 3] = data[base + 3];
    }
  }
  return output;
};

const applyAdjustments = (imageData, adjust) => {
  const data = imageData.data;
  const width = imageData.width;
  const height = imageData.height;
  const {
    brightness = 1,
    contrast = 1,
    saturation = 1,
    warmth = 0,
    blur = 0,
    temperature = 0,
    tint = 0,
    highlights = 0,
    shadows = 0,
    whites = 0,
    blacks = 0,
    vibrance = 0,
    invert = false,
    sharpness = 0,
    clarity = 0,
    vignette = 0,
  } = adjust;

  const tempShift = (temperature + warmth * 0.6) / 100;
  const tintShift = tint / 100;

  // Best-effort approximations for advanced controls (temperature/tint/highlights/shadows/etc.)
  // using per-pixel math. These are GPU-safe, client-only transforms intended for preview.
  for (let i = 0; i < data.length; i += 4) {
    let r = data[i];
    let g = data[i + 1];
    let b = data[i + 2];

    // Brightness/contrast baseline.
    r = (r - 128) * contrast + 128;
    g = (g - 128) * contrast + 128;
    b = (b - 128) * contrast + 128;

    r *= brightness;
    g *= brightness;
    b *= brightness;

    // Temperature/tint shifts (best-effort approximation).
    if (tempShift !== 0) {
      r += tempShift * 30;
      b -= tempShift * 30;
      g += tempShift * 10;
    }
    if (tintShift !== 0) {
      r += tintShift * 20;
      b += tintShift * 20;
      g -= tintShift * 20;
    }

    // Tonal adjustments based on luminance ranges (smoothstep).
    const luma = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
    [r, g, b] = applyToneAdjustment(r, g, b, luma, highlights, 0.6, 1, 0.35);
    [r, g, b] = applyToneAdjustment(r, g, b, luma, whites, 0.85, 1, 0.45);
    [r, g, b] = applyToneAdjustment(r, g, b, luma, shadows, 0, 0.4, 0.35);
    [r, g, b] = applyToneAdjustment(r, g, b, luma, blacks, 0, 0.15, 0.45);

    // Saturation/vibrance in HSL.
    let [h, s, l] = rgbToHsl(clamp(r, 0, 255), clamp(g, 0, 255), clamp(b, 0, 255));
    s = clamp(s * saturation, 0, 1);
    if (vibrance !== 0) {
      const v = vibrance / 100;
      const boost = v >= 0 ? (1 - s) : s;
      s = clamp(s + v * boost * 0.5, 0, 1);
    }
    [r, g, b] = hslToRgb(h, s, l);

    if (invert) {
      r = 255 - r;
      g = 255 - g;
      b = 255 - b;
    }

    data[i] = clamp(r, 0, 255);
    data[i + 1] = clamp(g, 0, 255);
    data[i + 2] = clamp(b, 0, 255);
  }

  if (sharpness !== 0 || clarity !== 0) {
    const blurRadius = 1;
    const blurred = boxBlur(data, width, height, blurRadius);
    const sharpnessAmount = clamp(sharpness / 100, 0, 1);
    const clarityAmount = clamp(clarity / 100, -1, 1) * 0.6;
    for (let i = 0; i < data.length; i += 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const hr = r - blurred[i];
      const hg = g - blurred[i + 1];
      const hb = b - blurred[i + 2];
      let nr = r + hr * sharpnessAmount;
      let ng = g + hg * sharpnessAmount;
      let nb = b + hb * sharpnessAmount;
      if (clarityAmount !== 0) {
        const luma = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
        const midtone = 1 - Math.abs(luma - 0.5) * 2;
        nr += hr * clarityAmount * midtone;
        ng += hg * clarityAmount * midtone;
        nb += hb * clarityAmount * midtone;
      }
      data[i] = clamp(nr, 0, 255);
      data[i + 1] = clamp(ng, 0, 255);
      data[i + 2] = clamp(nb, 0, 255);
    }
  }

  return imageData;
};

const applyVignette = (ctx, width, height, amount) => {
  if (!amount) return;
  const strength = (amount / 100) * 0.6;
  const cx = width / 2;
  const cy = height / 2;
  const maxDist = Math.sqrt(cx * cx + cy * cy);
  const imageData = ctx.getImageData(0, 0, width, height);
  const data = imageData.data;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const index = (y * width + x) * 4;
      const dx = x - cx;
      const dy = y - cy;
      const dist = Math.sqrt(dx * dx + dy * dy) / maxDist;
      const falloff = dist * dist;
      const factor = strength >= 0 ? 1 - strength * falloff : 1 + Math.abs(strength) * falloff;
      data[index] = clamp(data[index] * factor, 0, 255);
      data[index + 1] = clamp(data[index + 1] * factor, 0, 255);
      data[index + 2] = clamp(data[index + 2] * factor, 0, 255);
    }
  }
  ctx.putImageData(imageData, 0, 0);
};

export const renderAdjustedImageBitmap = ({
  imgEl,
  elementAdjust,
  crop,
  flipX,
  flipY,
  eraser,
  fit,
  targetWidth,
  targetHeight,
  maxSize = 2048,
  cacheKey,
}) => {
  if (!imgEl) return null;

  const key = cacheKey || buildAdjustCacheKey({
    src: imgEl.currentSrc || imgEl.src,
    crop,
    flipX,
    flipY,
    adjust: elementAdjust,
    eraser,
    targetWidth,
    targetHeight,
    maxSize,
    fit,
  });

  if (renderCache.has(key)) {
    return renderCache.get(key);
  }

  const scale = Math.min(1, maxSize / Math.max(targetWidth || 1, targetHeight || 1));
  const outputW = Math.max(1, Math.round((targetWidth || imgEl.naturalWidth) * scale));
  const outputH = Math.max(1, Math.round((targetHeight || imgEl.naturalHeight) * scale));

  const canvas = document.createElement('canvas');
  canvas.width = outputW;
  canvas.height = outputH;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  let sx = 0;
  let sy = 0;
  let sw = imgEl.naturalWidth;
  let sh = imgEl.naturalHeight;
  if (crop?.enabled) {
    const cropRect = crop;
    sx = cropRect.x * imgEl.naturalWidth;
    sy = cropRect.y * imgEl.naturalHeight;
    sw = cropRect.w * imgEl.naturalWidth;
    sh = cropRect.h * imgEl.naturalHeight;
  } else if (fit !== 'contain') {
    // Mimic background-size: cover when no crop is applied.
    const imgAspect = imgEl.naturalWidth / imgEl.naturalHeight;
    const outAspect = outputW / outputH;
    if (imgAspect > outAspect) {
      const desiredW = imgEl.naturalHeight * outAspect;
      sx = (imgEl.naturalWidth - desiredW) / 2;
      sw = desiredW;
    } else if (imgAspect < outAspect) {
      const desiredH = imgEl.naturalWidth / outAspect;
      sy = (imgEl.naturalHeight - desiredH) / 2;
      sh = desiredH;
    }
  }

  ctx.save();
  const blur = elementAdjust?.blur || 0;
  if (blur > 0) {
    ctx.filter = `blur(${Math.max(0, blur * scale)}px)`;
  }

  if (flipX || flipY) {
    ctx.translate(flipX ? outputW : 0, flipY ? outputH : 0);
    ctx.scale(flipX ? -1 : 1, flipY ? -1 : 1);
  }

  ctx.drawImage(imgEl, sx, sy, sw, sh, 0, 0, outputW, outputH);
  ctx.restore();
  ctx.filter = 'none';

  const baseImageData = ctx.getImageData(0, 0, outputW, outputH);
  const adjustCopy = new ImageData(
    new Uint8ClampedArray(baseImageData.data),
    outputW,
    outputH,
  );
  const adjusted = applyAdjustments(adjustCopy, elementAdjust || {});
  ctx.putImageData(adjusted, 0, 0);

  const magicOps = eraser?.magic?.ops || [];
  if (magicOps.length) {
    const originalW = imgEl.naturalWidth || outputW;
    const originalH = imgEl.naturalHeight || outputH;
    const mappedOps = magicOps
      .map((op) => {
        const ox = clamp(op.x ?? 0.5, 0, 1) * originalW;
        const oy = clamp(op.y ?? 0.5, 0, 1) * originalH;
        const relX = (ox - sx) / sw;
        const relY = (oy - sy) / sh;
        if (Number.isNaN(relX) || Number.isNaN(relY)) return null;
        if (relX < 0 || relX > 1 || relY < 0 || relY > 1) return null;
        const mappedX = flipX ? 1 - relX : relX;
        const mappedY = flipY ? 1 - relY : relY;
        return {
          ...op,
          x: clamp(mappedX, 0, 1),
          y: clamp(mappedY, 0, 1),
        };
      })
      .filter(Boolean);

    if (mappedOps.length) {
      const magicKey = buildMagicMaskKey({
        ops: mappedOps,
        crop: { enabled: false, x: 0, y: 0, w: 1, h: 1 },
        flipX: false,
        flipY: false,
        width: outputW,
        height: outputH,
      });
      let maskCanvas = getMaskCache(magicKey);
      if (!maskCanvas) {
        const magicMask = buildMagicMask({
          imageData: baseImageData,
          width: outputW,
          height: outputH,
          ops: mappedOps,
        });
        if (magicMask) {
          const nextMaskCanvas = document.createElement('canvas');
          nextMaskCanvas.width = outputW;
          nextMaskCanvas.height = outputH;
          const maskCtx = nextMaskCanvas.getContext('2d');
          if (maskCtx) {
            const maskImage = maskCtx.createImageData(outputW, outputH);
            for (let i = 0; i < magicMask.length; i += 1) {
              const offset = i * 4;
              const alpha = magicMask[i];
              maskImage.data[offset] = 255;
              maskImage.data[offset + 1] = 255;
              maskImage.data[offset + 2] = 255;
              maskImage.data[offset + 3] = alpha;
            }
            maskCtx.putImageData(maskImage, 0, 0);
            maskCanvas = nextMaskCanvas;
            setMaskCache(magicKey, maskCanvas);
          }
        }
      }
      if (maskCanvas) {
        ctx.save();
        ctx.globalCompositeOperation = 'destination-in';
        ctx.drawImage(maskCanvas, 0, 0);
        ctx.restore();
      }
    }
  }

  if (eraser?.strokes?.length) {
    ctx.save();
    ctx.globalCompositeOperation = 'destination-out';
    const scaleX = outputW / targetWidth;
    const scaleY = outputH / targetHeight;
    eraser.strokes.forEach((stroke) => {
      if (!stroke.points?.length) return;
      ctx.lineWidth = (stroke.size || 12) * scaleX;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = `rgba(0,0,0,${stroke.opacity ?? 1})`;
      ctx.beginPath();
      stroke.points.forEach((pt, index) => {
        const x = (flipX ? 1 - pt.x : pt.x) * outputW;
        const y = (flipY ? 1 - pt.y : pt.y) * outputH;
        if (index === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      });
      ctx.stroke();
    });
    ctx.restore();
  }

  if (elementAdjust?.vignette) {
    applyVignette(ctx, outputW, outputH, elementAdjust.vignette);
  }

  const result = {
    canvas,
    dataUrl: canvas.toDataURL('image/png'),
    width: outputW,
    height: outputH,
  };

  renderCache.set(key, result);
  if (renderCache.size > CACHE_LIMIT) {
    const firstKey = renderCache.keys().next().value;
    renderCache.delete(firstKey);
  }
  return result;
};
